import React, { useState } from "react";
import {
  TouchableOpacity,
  StyleSheet,
  TextInput,
  View,
  Modal,
  Text,
} from "react-native";

import { renameLabel } from "../../../utils/common";
import { useDispatch, useSelector } from "react-redux";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";
import {
  filterTrackerOrders,
  filterTrackerOrdersMulti,
  resetSearch,
} from "../../../redux/reducers/productTrackerReducer";

import {
  filterTrackerOrders as filterChinaOrders,
  resetSearch as resetChinaSearch,
} from "../../../redux/reducers/chinaTrackerReducer";
import { useTranslation } from "../../../utils/useTranslation";

interface Props {
  itemsList: Array<string>;
  filterCategory: string;
  setFilterCategory: (category: string) => void;
  tableHeaders: any; // Add tableHeaders prop if it's not global
  trackerType: string;
  productionLine?: any;
  isCollapsible?: boolean;
  isExpanded?: boolean;
  setIsExpanded?: (expanded: boolean) => void;
  collapseTriggerLabel?: string;
  collapseOnSelect?: boolean;
  // Multi-select
  multiSelect?: boolean;
  selectedCategories?: string[];
  setSelectedCategories?: (categories: string[]) => void;
}

export const ItemTrackerBadgeHorizontalList: React.FC<Props> = ({
  itemsList,
  filterCategory,
  setFilterCategory,
  tableHeaders,
  trackerType,
  productionLine,
  isCollapsible = false,
  isExpanded = false,
  setIsExpanded,
  collapseTriggerLabel = "All",
  collapseOnSelect = false,
  multiSelect = false,
  selectedCategories = [],
  setSelectedCategories,
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch<any>();
  const language = useSelector((state: any) => state.configurations.language);
  const { t } = useTranslation();

  const handleDateChange = (key: "from" | "to", value: string) => {
    setDateRange((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilter = (filter: string) => {
    setFilterCategory(filter);
  };

  const validateAndApply = () => {
    if (!dateRange.from || !dateRange.to) {
      setErrorMessage("Both 'From' and 'To' dates are required.");
      return;
    }

    handleCustomDateApply(dateRange);
    setIsModalVisible(false);
  };

  const handleCustomDateApply = (dateRange: any) => {
    if (dateRange.from && dateRange.to) {
      const customFilter = `From: ${dateRange.from} To: ${dateRange.to}`;
      applyFilter(customFilter);
    }
    setIsModalVisible(false);
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};
    filters = {
      dateRange: dateRange,
    };
  };

  const handleBadgeClick = (item: string) => {
    const today = new Date();
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};

    if (item === "Custom Date Range") {
      setIsModalVisible(true); // Open modal for custom date selection
      return;
    } else if (item === "Last 3 months") {
      const threeMonthsAgo = new Date(
        today.getFullYear(),
        today.getMonth() - 3,
        today.getDate(),
      );
      setDateRange({
        from: threeMonthsAgo.toISOString().split("T")[0],
        to: today.toISOString().split("T")[0],
      });
      filters = {
        dateRange: {
          from: threeMonthsAgo.toISOString().split("T")[0],
          to: today.toISOString().split("T")[0],
        },
      };
    } else {
      filters = { status: item };
    }

    // Multi-select mode for CHINA tracker
    if (multiSelect && setSelectedCategories) {
      const isAll = item === "All" || item === t("all");
      let newCategories: string[];

      if (isAll) {
        // Clicking "All" clears multi-select
        newCategories = [];
        setSelectedCategories(newCategories);
        applyFilter(item);
        if (trackerType === "CUSTOMER") {
          dispatch(resetSearch());
        } else {
          dispatch(
            resetChinaSearch({
              item: item,
              productionLine: productionLine,
              language: language,
            }),
          );
        }
      } else {
        // Toggle the item in/out of selected categories
        if (selectedCategories.includes(item)) {
          newCategories = selectedCategories.filter((c) => c !== item);
        } else {
          newCategories = [...selectedCategories, item];
        }
        setSelectedCategories(newCategories);

        if (newCategories.length === 0) {
          // No filters selected = show all
          applyFilter(t("all") || "All");
          if (trackerType === "CUSTOMER") {
            dispatch(resetSearch());
          } else {
            dispatch(
              resetChinaSearch({
                item: t("all") || "All",
                productionLine: productionLine,
                language: language,
              }),
            );
          }
        } else {
          applyFilter(newCategories.join(","));
          if (trackerType === "CUSTOMER") {
            dispatch(filterTrackerOrdersMulti(newCategories));
          } else {
            dispatch(
              filterChinaOrders({
                items: newCategories,
                productionLine: productionLine,
                language: language,
              }),
            );
          }
        }
      }

      if (collapseOnSelect && setIsExpanded) {
        setIsExpanded(false);
      }
      return;
    }

    // Single-select mode (original behavior)
    applyFilter(item); // Update the selected badge visually
    if (item === "All" || item === t("all")) {
      trackerType === "CUSTOMER"
        ? dispatch(resetSearch())
        : dispatch(
          resetChinaSearch({
            item: item,
            productionLine: productionLine,
            language: language,
          }),
        );
    } else {
      trackerType === "CUSTOMER"
        ? dispatch(filterTrackerOrders(item))
        : dispatch(
          filterChinaOrders({
            item: item,
            productionLine: productionLine,
            language: language,
          }),
        );
    }

    if (collapseOnSelect && setIsExpanded) {
      setIsExpanded(false);
    }
  };

  const isBadgeActive = (category: string): boolean => {
    if (multiSelect && selectedCategories.length > 0) {
      return selectedCategories.includes(category);
    }
    if (multiSelect && selectedCategories.length === 0) {
      return category === t("all") || category === "All";
    }
    return filterCategory === category;
  };

  return (
    <View style={styles.badgesRow}>
      {isCollapsible ? (
        isExpanded ? (
          <>
            {itemsList.map((category, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.filterButton,
                  isBadgeActive(category) && styles.activeFilterButton,
                ]}
                onPress={() => handleBadgeClick(category)}
              >
                <Text
                  style={
                    isBadgeActive(category)
                      ? styles.activeFilterText
                      : styles.filterText
                  }
                >
                  {renameLabel(category)}
                </Text>
              </TouchableOpacity>
            ))}
          </>
        ) : (
          <TouchableOpacity
            key={"collapsed"}
            style={[styles.filterButton, styles.activeFilterButton]}
            onPress={() => setIsExpanded && setIsExpanded(true)}
          >
            <Text style={styles.activeFilterText}>
              {multiSelect && selectedCategories.length > 0
                ? selectedCategories.length === 1
                  ? renameLabel(selectedCategories[0])
                  : `${selectedCategories.length} selected`
                : renameLabel(
                  filterCategory && filterCategory.length > 0
                    ? filterCategory
                    : collapseTriggerLabel,
                )}
            </Text>
          </TouchableOpacity>
        )
      ) : (
        itemsList.map((category, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.filterButton,
              isBadgeActive(category) && styles.activeFilterButton,
            ]}
            onPress={() => handleBadgeClick(category)}
          >
            <Text
              style={
                isBadgeActive(category)
                  ? styles.activeFilterText
                  : styles.filterText
              }
            >
              {renameLabel(category)}
            </Text>
          </TouchableOpacity>
        ))
      )}

      {/* Custom Date Range Modal */}
      <Modal
        visible={isModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Custom Date Range</Text>

            {/* From Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select From Date"
              value={dateRange.from}
              onChangeText={(value) => handleDateChange("from", value)}
              onFocus={(e) => ((e.currentTarget as any).type = "date")} // Show date picker on focus
            />

            {/* To Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select To Date"
              value={dateRange.to}
              onChangeText={(value) => handleDateChange("to", value)}
              onFocus={(e) => ((e.currentTarget as any).type = "date")} // Show date picker on focus
            />

            {/* Error Message */}
            {errorMessage ? (
              <Text style={styles.errorMessage}>{errorMessage}</Text>
            ) : null}

            {/* Modal Buttons */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setIsModalVisible(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.applyButton}
                onPress={validateAndApply}
              >
                <Text style={styles.applyButtonText}>Apply</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      paddingHorizontal: 10,
    },
    badgesRow: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    badgeStyle: {
      marginHorizontal: 4,
      fontSize: 12,
      paddingHorizontal: 10,
    },
    inActiveBadge: {
      color: "black",
      backgroundColor: "rgba(0, 0, 0, 0.12)",
    },
    activeBadge: {
      color: "white",
      backgroundColor: "rgb(47, 149, 220)",
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      justifyContent: "center",
      alignItems: "center",
    },
    modalContent: {
      backgroundColor: colors.surface,
      width: "90%",
      maxWidth: 400,
      borderRadius: 12,
      padding: 24,
      alignItems: "center",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 10,
      elevation: 5,
    },
    modalTitle: {
      fontSize: 20,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 20,
      textAlign: "center",
    },
    dateInput: {
      width: "100%",
      backgroundColor: colors.background,
      borderRadius: 8,
      paddingHorizontal: 16,
      paddingVertical: 12,
      marginBottom: 16,
      fontSize: 15,
      color: colors.text,
    },
    modalActions: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      marginTop: 8,
      gap: 12,
    },
    cancelButton: {
      flex: 1,
      backgroundColor: colors.surface,
      borderColor: colors.textMuted,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
    },
    cancelButtonText: {
      color: "#374151",
      fontSize: 15,
      fontWeight: "600",
    },
    applyButton: {
      flex: 1,
      backgroundColor: "#2563EB",
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
    },
    applyButtonText: {
      color: "#FFFFFF",
      fontSize: 15,
      fontWeight: "600",
    },
    errorMessage: {
      color: "red",
      marginBottom: 10,
      textAlign: "center",
    },
    filterMainContainer: {
      width: "100%",
    },
    filterContainer: {
      flexDirection: "row",
      paddingVertical: 10,
      paddingHorizontal: 15,
    },
    filterButton: {
      paddingVertical: 6,
      paddingHorizontal: 14,
      backgroundColor: colors.surfaceSubtle,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 999,
      marginRight: 8,
      marginBottom: 6,
    },
    activeFilterButton: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    filterText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    activeFilterText: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600",
    },
  });
}
